declare const process: { env: Record<string, string | undefined> };

const configuredPayloadAPIURL = process.env.PAYLOAD_API_URL ?? 'http://127.0.0.1:3070/payload';

/** Payload REST base URL, normalized for the historical `/api` development value. */
export const payloadAPIURL = configuredPayloadAPIURL
  .replace(/\/api\/?$/, '/payload')
  .replace(/\/$/, '');

/** Origin used to turn relative Payload Media paths into renderable URLs. */
export const payloadServerURL = payloadAPIURL.replace(/\/payload\/?$/, '');

export interface PayloadMediaLike {
  url?: string | null;
  alt?: string | null;
  width?: number | null;
  height?: number | null;
  mimeType?: string | null;
  filename?: string | null;
}

export interface ResolvedPayloadMedia {
  url: string;
  alt?: string;
  width?: number;
  height?: number;
  mimeType?: string;
  filename?: string;
}

function containsInvalidURLCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return character === '\\' || code < 32 || code === 127;
  });
}

function isLoopbackHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

export function resolvePayloadURL(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const url = value.trim();
  if (!url || url.startsWith('//') || containsInvalidURLCharacter(url)) return undefined;

  try {
    const hasScheme = /^[a-z][a-z\d+.-]*:/iu.test(url);
    const parsed = hasScheme
      ? new URL(url)
      : new URL(url.startsWith('/') ? url : `/${url}`, payloadServerURL);
    const isSafeProtocol =
      parsed.protocol === 'https:' ||
      (parsed.protocol === 'http:' && isLoopbackHostname(parsed.hostname));
    return isSafeProtocol ? parsed.toString() : undefined;
  } catch {
    return undefined;
  }
}

export function resolvePayloadMedia(
  value: unknown,
  options: { requireImage?: boolean } = {},
): ResolvedPayloadMedia | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const media = value as PayloadMediaLike;
  if (options.requireImage && !media.mimeType?.toLowerCase().startsWith('image/')) return undefined;
  const url = resolvePayloadURL(media.url);
  if (!url) return undefined;

  const alt = media.alt?.trim();
  const mimeType = media.mimeType?.trim();
  const filename = media.filename?.trim();
  return {
    url,
    ...(alt ? { alt } : {}),
    ...(typeof media.width === 'number' && media.width > 0 ? { width: media.width } : {}),
    ...(typeof media.height === 'number' && media.height > 0 ? { height: media.height } : {}),
    ...(mimeType ? { mimeType } : {}),
    ...(filename ? { filename } : {}),
  };
}
